import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPause } from "@fortawesome/free-solid-svg-icons";
import { themeContext } from "../../context/theme";
import { gameContext } from "../../context/game";
import { useBoardSkin, getSkinFromTheme, PREMIUM_BOARDS } from "../../context/boardSkin";
import { CardOne } from "../../components/cards/cardOne";
import { GoBackButton } from "../../components/buttons/goBack";
import { useNetworkService } from "../../hooks/useNetworkService";
import { useGamepad } from "../../hooks/useGamepad";
import { useKeyboard } from "../../hooks/useKeyboard";
import { ConfirmationModal } from "../../components/modals/ConfirmationModal";
import { OnlineStartTimer } from "./OnlineStartTimer";
import styles from "./OnlineGameArena.module.css";
import {
  tetrominoes,
  createPlayerBoardMatrix,
  deepCloneArray,
  defaultUserData,
} from "../../utilities/utilities";
import { OnlineGame } from "../../utilities/OnlineGame";
import { OnlineInGameStats } from "./OnlineInGameStats";
import { DisconnectionNotification } from "./DisconnectionNotification";
import toastService from "../../services/toastService";
import { InGameStats } from "../gamePage/inGameStats";
import { OnlineGameResult } from "./OnlineGameResult";
import { OnlineLineClearMultiplierDisplay } from "./OnlineLineClearMultiplierDisplay";
import TetrisBoard3D from "../../components/three/TetrisBoard3D";
import ThemeSoundManager from "../../utilities/ThemeSoundManager";
import { getThemeSoundConfig } from "../../components/three/themes";
import { getBlockColor } from "../../components/three/materials/SpectrumMaterial";

// Theme color map for 3D rendering
const THEME_COLORS = {
  0: '#ff6666',
  1: '#66ff66',
  2: '#ba68c8',
  3: '#ffff66',
  4: '#66ffff',
};

// Helper to get theme color from theme string/number
const getThemeColor = (theme) => {
  const themeNum = typeof theme === 'string' ? theme.replace('theme-', '') : theme;
  return THEME_COLORS[themeNum] || THEME_COLORS[0];
};

// Spectrum skin accent color - matches the 3D aesthetic
const SPECTRUM_BORDER_COLOR = '#00FFFF'; // Cyan to match the tetromino palette

// Creates complete 3D player JSX - entirely separate from 2D rendering
const create3DPlayerView = (onlineGame, playerId, gameState, theme, borderColor = SPECTRUM_BORDER_COLOR, skinName = 'Spectrum') => {
  if (!onlineGame?.player) {
    return <div style={{ color: 'white' }}>Loading player data...</div>;
  }

  const playerName = gameState?.players?.find((p) => p.id === playerId)?.name || "Player";
  const currentScore = onlineGame?.getGameStats()?.score || 0;
  const currentLifeSaverCount = onlineGame?.player?.lifeSaverCount || 0;
  const currentTetromino = onlineGame.getCurrentTetromino();
  const playerBoardMatrix = onlineGame.getBoardMatrix();
  const playerBoardVersion = onlineGame.getBoardVersion();
  const hardDropPreview = onlineGame.getHardDropPreview();
  
  // 3D skin border colors:
  // - Spectrum: cycles based on current piece color (dynamic)
  // - Blaze/Cyan: static signature colors
  // - Default: plain muted gray, no glow
  const skinBorderColors = {
    'Default': { border: '#666666', glow: 'transparent', static: true },  // Plain gray - no glow
    'Blaze': { border: '#B22222', glow: '#DC143C', static: true },   // Fire red - static
    'Cyan': { border: '#00FFFF', glow: '#40E0D0', static: true },    // Ice cyan - static
    'Spectrum': { border: null, glow: null, static: false },         // Uses piece color
  };
  const skinConfig = skinBorderColors[skinName] || skinBorderColors['Spectrum'];

  // For Spectrum: use current piece color. For Blaze/Cyan/Default: use static skin color
  const skinBorderColor = skinConfig.static
    ? skinConfig.border
    : (borderColor || '#00CED1');  // borderColor comes from current piece
  const skinGlowColor = skinConfig.static
    ? skinConfig.glow
    : (borderColor || '#00CED1');

  const labelStyle = {
    fontSize: '8px',
    fontWeight: 'bold',
    fontStyle: 'italic',
    fontFamily: '"Courier New", monospace',
    color: '#aaa',
    marginTop: '1px',
    letterSpacing: '0.5px'
  };

  // Always use the board's border color for all 3D boards (Inferno=red, Frost=cyan, Spectrum=dynamic, Default=gray)
  const scoreColor = skinBorderColor;

  const valueStyle = {
    fontSize: '14px',
    fontWeight: 'bold',
    fontStyle: 'italic',
    fontFamily: '"Courier New", monospace',
    color: scoreColor,
    lineHeight: 1,
    textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      {/* Score Display */}
      <div style={{ display: 'flex', gap: '15px', marginBottom: '5px', marginLeft: '2px' }}>
        <div>
          <div style={valueStyle}>{currentScore.toLocaleString()}</div>
          <div style={labelStyle}>SCORE</div>
        </div>
        <div>
          <div style={valueStyle}>{currentLifeSaverCount}</div>
          <div style={labelStyle}>LIFE SAVERS</div>
        </div>
      </div>

      {/* Board container with side stats - matches 2D .container structure */}
      <div style={{
        display: 'flex',
        alignItems: 'stretch',
        position: 'relative',
        backgroundColor: 'rgba(12, 12, 12)',
        border: `2px solid ${skinBorderColor}`,
        boxShadow: skinName === 'Default' ? 'none' : `0 0 5px ${skinGlowColor}`,
        borderRadius: '8px'
      }}>
        {/* Line Clear Multipliers - Left of Board */}
        <OnlineLineClearMultiplierDisplay
          player={true}
          onlineGame={onlineGame}
          dynamicColor={skinBorderColor}  // Always pass the color (dynamic or static)
          boardSkin={skinName?.toLowerCase()}
        />

        {/* 3D Board - 200x400 matches 2D dimensions (10 cols × 20px, 20 rows × 20px) */}
        <div style={{
          width: '200px',
          height: '400px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <TetrisBoard3D
            boardMatrix={playerBoardMatrix}
            boardVersion={playerBoardVersion}
            currentPiece={currentTetromino}
            hardDropPreview={hardDropPreview}
            isLocalPlayer={true}
            onlineGame={onlineGame}
            skinName={skinName}
          />
          {onlineGame?.player?.isGameOver && (
            <div style={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              backdropFilter: 'blur(3px)'
            }}>
              <div style={{
                color: 'white',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                textShadow: '0 0 15px rgba(255,0,0,0.8)'
              }}>
                💀 Game Over
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Player Name */}
      <div style={{ marginTop: '10px', marginLeft: '2px' }}>
        <div style={{
          ...valueStyle,
          width: '150px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {playerName}
        </div>
      </div>
    </div>
  );
};

// Creates complete 3D opponent JSX - entirely separate from 2D rendering
const create3DOpponentView = (opponent, opponentTheme, gameState, roomData, borderColor = SPECTRUM_BORDER_COLOR, skinName = 'Spectrum', opponentSimulator = null) => {
  const opponentName =
    opponent.name ||
    gameState?.players?.find((p) => p.id === opponent.id)?.name ||
    roomData?.players?.find((p) => p.id === opponent.id)?.name ||
    `Player ${opponent.id?.slice(0, 8)}`;
  const opponentScore = opponent.score || 0;
  const opponentLifeSaverCount = opponent.lifeSaverCount || 0;

  // 3D skin border colors:
  // - Spectrum: cycles based on current piece color (dynamic)
  // - Blaze/Cyan: static signature colors
  // - Default: plain muted gray, no glow
  const skinBorderColors = {
    'Default': { border: '#666666', glow: 'transparent', static: true },  // Plain gray - no glow
    'Blaze': { border: '#B22222', glow: '#DC143C', static: true },   // Fire red - static
    'Cyan': { border: '#00FFFF', glow: '#40E0D0', static: true },    // Ice cyan - static
    'Spectrum': { border: null, glow: null, static: false },         // Uses piece color
  };
  const skinConfig = skinBorderColors[skinName] || skinBorderColors['Spectrum'];

  // For Spectrum: use current piece color. For Blaze/Cyan/Default: use static skin color
  const skinBorderColor = skinConfig.static
    ? skinConfig.border
    : (borderColor || '#00CED1');  // borderColor comes from current piece
  const skinGlowColor = skinConfig.static
    ? skinConfig.glow
    : (borderColor || '#00CED1');

  const labelStyle = {
    fontSize: '8px',
    fontWeight: 'bold',
    fontStyle: 'italic',
    fontFamily: '"Courier New", monospace',
    color: '#aaa',
    marginTop: '1px',
    letterSpacing: '0.5px'
  };

  // Always use the board's border color for all 3D boards (Inferno=red, Frost=cyan, Spectrum=dynamic, Default=gray)
  const scoreColor = skinBorderColor;

  const valueStyle = {
    fontSize: '14px',
    fontWeight: 'bold',
    fontStyle: 'italic',
    fontFamily: '"Courier New", monospace',
    color: scoreColor,
    lineHeight: 1,
    textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      {/* Score Display */}
      <div style={{ display: 'flex', gap: '15px', marginBottom: '5px', marginLeft: '2px' }}>
        <div>
          <div style={valueStyle}>{opponentScore.toLocaleString()}</div>
          <div style={labelStyle}>SCORE</div>
        </div>
        <div>
          <div style={valueStyle}>{opponentLifeSaverCount}</div>
          <div style={labelStyle}>LIFE SAVERS</div>
        </div>
      </div>

      {/* Board container with side stats - matches 2D .container structure */}
      <div style={{
        display: 'flex',
        alignItems: 'stretch',
        position: 'relative',
        backgroundColor: 'rgba(12, 12, 12)',
        border: `2px solid ${skinBorderColor}`,
        boxShadow: skinName === 'Default' ? 'none' : `0 0 5px ${skinGlowColor}`,
        borderRadius: '8px'
      }}>
        {/* Line Clear Multipliers - Left of Board */}
        <OnlineLineClearMultiplierDisplay
          opponent={opponent}
          opponentTheme={opponentTheme}
          dynamicColor={skinBorderColor}  // Always pass the color (dynamic or static)
          boardSkin={skinName?.toLowerCase()}
        />

        {/* 3D Board - 200x400 matches 2D dimensions (10 cols × 20px, 20 rows × 20px) */}
        <div style={{
          width: '200px',
          height: '400px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <TetrisBoard3D
            boardMatrix={opponent.board}
            boardVersion={opponent.boardVersion || 0}
            currentPiece={opponent.currentPiece}
            hardDropPreview={opponent.hardDropPreview}
            isLocalPlayer={false}
            onlineGame={opponentSimulator}
            skinName={skinName}
          />
          {opponent.isGameOver && (
            <div style={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              backdropFilter: 'blur(3px)'
            }}>
              <div style={{
                color: 'white',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                textShadow: '0 0 15px rgba(255,0,0,0.8)'
              }}>
                💀 Game Over
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Player Name */}
      <div style={{ marginTop: '10px', marginLeft: '2px' }}>
        <div style={{
          ...valueStyle,
          width: '150px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {opponentName}
        </div>
      </div>
    </div>
  );
};

// Online Player component - shows the actual local game state
const OnlinePlayer = ({ onlineGame, playerId, gameState, onGameEvent }) => {
  const { theme } = useContext(themeContext);
  const { skinName, selectedPremiumBoard } = useBoardSkin();
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const animationRef = useRef();
  const [previousScore, setPreviousScore] = useState(0);

  // Track score changes for animation (sound removed)
  useEffect(() => {
    const currentScore = onlineGame?.getGameStats()?.score || 0;
    setPreviousScore(currentScore);
  }, [onlineGame?.getGameStats()?.score]);

  useEffect(() => {
    if (!onlineGame?.isGameStarted) return; // Only animate if game has started

    const animate = () => {
      forceUpdate(); // Force re-render
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [onlineGame?.isGameStarted]); // Dependency on game started state

  // Persistent border color logic for smooth transitions
  const borderColorRef = useRef(SPECTRUM_BORDER_COLOR);
  const currentTetromino = onlineGame?.getCurrentTetromino();
  
  if (currentTetromino) {
    borderColorRef.current = getBlockColor(currentTetromino.colorClass);
  }

  // Always use 3D rendering — skinName is "Default" when no premium board is selected
  if (skinName) {
    return create3DPlayerView(onlineGame, playerId, gameState, theme, borderColorRef.current, skinName);
  }

  // 2D mode - use existing createGameBox and CSS-based rendering
  const createGameBox = () => {
    if (!onlineGame) return null;

    const currentTetromino = onlineGame.getCurrentTetromino();
    const playerBoardMatrix = onlineGame.getBoardMatrix();
    const hardDropPreview = onlineGame.getHardDropPreview();

    return (
      <>
        {Array.from({ length: 20 }).map((_, i) => (
          <div className={styles.row} key={i}>
            <span
              className={styles.laserBeam}
              id={`online-player-laser-beam-${i}`}
            ></span>
            {Array.from({ length: 10 }).map((_, j) => {
              let className = styles.column;
              const isCurrentPiece = currentTetromino?.allCoordinates?.some(
                (coord) => coord[0] === i && coord[1] === j && coord[0] >= 0 // Only render if row >= 0
              );
              const isHardDropPreview = hardDropPreview?.allCoordinates?.some(
                (coord) => coord[0] === i && coord[1] === j && coord[0] >= 0
              );
              const boardValue = playerBoardMatrix?.[i]?.[j];

              if (isCurrentPiece) {
                const colorClassMap = {
                  "l-tetromino-active": "l-tetromino-active",
                  "t-tetromino-active": "t-tetromino-active",
                  "square-tetromino-active": "square-tetromino-active",
                  "line-tetromino-active": "line-tetromino-active",
                  "z-tetromino-active": "z-tetromino-active",
                };

                const cssClass =
                  colorClassMap[currentTetromino.colorClass] ||
                  "l-tetromino-active";
                const themeNumber = theme.toString().replace("theme-", ""); // Extract number from "theme-0"
                const styleKey = cssClass + "-theme-" + themeNumber;
                const finalClassName = styles[styleKey];

                // Removed CSS debug logging to prevent massive spam (was logging for every tetromino cell)

                if (finalClassName) {
                  className += ` ${finalClassName}`;
                }
              } else if (isHardDropPreview) {
                className += ` ${styles.hardDropActive}`;
              } else if (boardValue && boardValue !== 0) {
                // Handle filled board cells with proper colors
                const colorClassMap = {
                  "l-tetromino-active": "l-tetromino-active",
                  "t-tetromino-active": "t-tetromino-active",
                  "square-tetromino-active": "square-tetromino-active",
                  "line-tetromino-active": "line-tetromino-active",
                  "z-tetromino-active": "z-tetromino-active",
                };

                const cssClass = colorClassMap[boardValue] || boardValue;
                const themeNumber = theme.toString().replace("theme-", "");
                const filledClassName =
                  styles[cssClass + "-theme-" + themeNumber];

                if (filledClassName) {
                  className += ` ${filledClassName}`;
                }
              }

              return <div className={className} key={j} />;
            })}
          </div>
        ))}
      </>
    );
  };

  if (!onlineGame?.player) {
    return <div>Loading player data...</div>;
  }

  const playerName =
    gameState?.players?.find((p) => p.id === playerId)?.name || "Player";
  const currentScore = onlineGame?.getGameStats()?.score || 0;
  const currentLifeSaverCount = onlineGame?.player?.lifeSaverCount || 0;

  return (
    <div className={styles.playerContent}>
      {/* Score Display Above Board */}
      <div className={styles.scoreContainer}>
        <div
          className={`${styles.scoreDisplay} ${
            styles[`scoreDisplay-theme-${theme}`]
          }`}
        >
          <div className={styles.scoreValue} key={currentScore}>
            {currentScore.toLocaleString()}
          </div>
          <div className={styles.scoreLabel}>SCORE</div>
        </div>
        <div
          className={`${styles.scoreDisplay} ${
            styles[`scoreDisplay-theme-${theme}`]
          }`}
        >
          <div className={styles.scoreValue} key={currentLifeSaverCount}>
            {currentLifeSaverCount}
          </div>
          <div className={styles.scoreLabel}>LIFE SAVERS</div>
        </div>
      </div>

      {/* Game board with theme border */}
      <div
        className={`${styles.container} ${styles[`container-theme-${theme}`]}`}
      >
        {/* Line Clear Multipliers Left of Board */}
        <OnlineLineClearMultiplierDisplay
          player={true}
          onlineGame={onlineGame}
        />
        <div className={styles.gameBoard}>
          {createGameBox()}
          {onlineGame?.player?.isGameOver && (
            <div className={styles.localGameOverOverlay}>
              <div className={styles.localGameOverText}>💀 Game Over</div>
            </div>
          )}
        </div>
      </div>

      {/* Player Name Below Board */}
      <div className={styles.playerNameContainer}>
        <div
          className={`${styles.playerName} ${
            styles[`playerName-theme-${theme}`]
          }`}
        >
          {playerName}
        </div>
      </div>
    </div>
  );
};

// OpponentPanel component - shows opponent boards
const OpponentPanel = ({
  opponent,
  opponentTheme = "theme-1",
  opponentBoardSkin = null,  // 3D skin for this opponent
  gameState,
  roomData,
  onlineGame,  // Added to access opponentSimulators for 3D effects
}) => {
  // Normalize theme to ensure it's always just the number (0, 1, 2, 3, 4, 5)
  const themeNumber = typeof opponentTheme === 'string'
    ? opponentTheme.replace('theme-', '')
    : opponentTheme.toString();

  // Persistent border color logic for smoother transitions
  const borderColorRef = useRef(SPECTRUM_BORDER_COLOR);
  if (opponent?.currentPiece) {
    borderColorRef.current = getBlockColor(opponent.currentPiece.colorClass);
  }

  // If 3D mode is enabled for this opponent, use completely separate 3D rendering
  if (opponentBoardSkin) {
    // Get the simulator for this opponent to enable 3D effects
    const opponentSimulator = onlineGame?.opponentSimulators?.get(opponent?.id);
    return create3DOpponentView(opponent, opponentTheme, gameState, roomData, borderColorRef.current, opponentBoardSkin, opponentSimulator);
  }

  // 2D mode - use existing CSS-based rendering
  const createOpponentBoard = (board, currentPiece, hardDropPreview) => {
    return (
      <>
        {Array.from({ length: 20 }).map((_, i) => (
          <div className={styles.row} key={i}>
            <span
              className={styles.laserBeam}
              id={`opponent-${opponent.id}-laser-beam-${i}`}
            ></span>
            {Array.from({ length: 10 }).map((_, j) => {
              let className = styles.column;
              const isCurrentPiece = currentPiece?.allCoordinates?.some(
                (coord) => coord[0] === i && coord[1] === j && coord[0] >= 0
              );
              const isHardDropPreview = hardDropPreview?.allCoordinates?.some(
                (coord) => coord[0] === i && coord[1] === j && coord[0] >= 0
              );
              const boardValue = board?.[i]?.[j];

              if (isCurrentPiece) {
                const colorClassMap = {
                  "l-tetromino-active": "l-tetromino-active",
                  "t-tetromino-active": "t-tetromino-active",
                  "square-tetromino-active": "square-tetromino-active",
                  "line-tetromino-active": "line-tetromino-active",
                  "z-tetromino-active": "z-tetromino-active",
                };

                const cssClass =
                  colorClassMap[currentPiece.colorClass] ||
                  "l-tetromino-active";
                const styleKey = cssClass + "-theme-" + themeNumber;
                const finalClassName = styles[styleKey];

                if (finalClassName) {
                  className += ` ${finalClassName}`;
                }
              } else if (isHardDropPreview) {
                className += ` ${styles.hardDropActive}`;
              } else if (boardValue && boardValue !== 0 && boardValue !== "") {
                const colorClassMap = {
                  "l-tetromino-active": "l-tetromino-active",
                  "t-tetromino-active": "t-tetromino-active",
                  "square-tetromino-active": "square-tetromino-active",
                  "line-tetromino-active": "line-tetromino-active",
                  "z-tetromino-active": "z-tetromino-active",
                };

                const cssClass = colorClassMap[boardValue] || boardValue;
                const filledClassName = styles[cssClass + "-theme-" + themeNumber];

                if (filledClassName) {
                  className += ` ${filledClassName}`;
                }
              }

              return <div className={className} key={j} />;
            })}
          </div>
        ))}
      </>
    );
  };

  const opponentName =
    opponent.name ||
    gameState?.players?.find((p) => p.id === opponent.id)?.name ||
    roomData?.players?.find((p) => p.id === opponent.id)?.name ||
    `Player ${opponent.id?.slice(0, 8)}`;
  const opponentScore = opponent.score || 0;
  const opponentLifeSaverCount = opponent.lifeSaverCount || 0;

  return (
    <div className={styles.playerContent}>
      {/* Score Display Above Board */}
      <div className={styles.scoreContainer}>
        <div
          className={`${styles.scoreDisplay} ${
            styles[`scoreDisplay-theme-${themeNumber}`]
          }`}
        >
          <div className={styles.scoreValue} key={opponentScore}>
            {opponentScore.toLocaleString()}
          </div>
          <div className={styles.scoreLabel}>SCORE</div>
        </div>
        <div
          className={`${styles.scoreDisplay} ${
            styles[`scoreDisplay-theme-${themeNumber}`]
          }`}
        >
          <div className={styles.scoreValue} key={opponentLifeSaverCount}>
            {opponentLifeSaverCount}
          </div>
          <div className={styles.scoreLabel}>LIFE SAVERS</div>
        </div>
      </div>

      {/* Opponent board with theme border - same as local player */}
      <div
        className={`${styles.container} ${
          styles[`container-theme-${themeNumber}`]
        }`}
      >
        {/* Line Clear Multipliers Left of Board */}
        <OnlineLineClearMultiplierDisplay
          opponent={opponent}
          opponentTheme={`theme-${themeNumber}`}
        />
        <div className={styles.gameBoard}>
          {createOpponentBoard(
            opponent.board,
            opponent.currentPiece,
            opponent.hardDropPreview
          )}
          {opponent.isGameOver && (
            <div className={styles.localGameOverOverlay}>
              <div className={styles.localGameOverText}>💀 Game Over</div>
            </div>
          )}
        </div>
      </div>

      {/* Player Name Below Board */}
      <div className={styles.playerNameContainer}>
        <div
          className={`${styles.playerName} ${
            styles[`playerName-theme-${themeNumber}`]
          }`}
        >
          {opponentName}
        </div>
      </div>
    </div>
  );
};

export const OnlineGameArena = ({ roomData, onBack, game }) => {
  const [isPaused, setIsPaused] = useState(false);
  const [gameState, setGameState] = useState(roomData?.gameState);
  const [showGameResult, setShowGameResult] = useState(false);
  const [gameResults, setGameResults] = useState(null);
  const [localPlayerGameOver, setLocalPlayerGameOver] = useState(false);
  const [opponentRenderTrigger, setOpponentRenderTrigger] = useState(0);
  const [disconnectionNotification, setDisconnectionNotification] =
    useState(null);
  const [showQuitModal, setShowQuitModal] = useState(false);
  const [playerThemes, setPlayerThemes] = useState(() => {
    // Initialize with roomData player themes
    const themes = {};
    roomData?.players?.forEach((player) => {
      themes[player.id] = player.theme || "theme-1";
    });
    return themes;
  });
  const { theme } = useContext(themeContext);
  const { playerId, sendGameEvent } = useNetworkService();
  const { skinName, selectedPremiumBoard } = useBoardSkin();

  // Load user saved data for controller bindings
  const [userSavedData] = useState(() => {
    try {
      const savedData = localStorage.getItem("userDataOne");
      return savedData ? JSON.parse(savedData) : defaultUserData;
    } catch (error) {
      return defaultUserData;
    }
  });

  // Ensure local player theme is always synced
  useEffect(() => {
    if (playerId && theme) {
      setPlayerThemes((prev) => ({
        ...prev,
        [playerId]: theme,
      }));
    }
  }, [playerId, theme]);
  const gameRef = useRef(null);

  // Get all players for background rendering - MUST be before any early returns
  // Don't depend on onlineGame here to avoid hook dependency issues
  const allPlayersWithThemes = useMemo(() => {
    const players = [];

    // Add local player - ensure theme consistency by using the same source
    const localPlayerName =
      gameState?.players?.find((p) => p.id === playerId)?.name || "You";
    const localPlayerTheme = playerThemes[playerId] || theme; // Use playerThemes first, fallback to themeContext
    players.push({
      id: playerId,
      name: localPlayerName,
      theme: localPlayerTheme,
      isLocal: true,
    });

    // Add opponents from initial gameState (not live onlineGame data)
    const opponents =
      gameState?.players?.filter((player) => player.id !== playerId) || [];

    opponents.forEach((opponent) => {
      // Get opponent's theme for UI colors - prefer gameState.players.theme (now includes theme)
      const opponentTheme = opponent.theme || playerThemes[opponent.id] || "theme-1";
      // Get opponent's selected board skin - prefer gameState.players.boardSkin (now includes boardSkin)
      const opponentBoardSkin = opponent.boardSkin || roomData?.players?.find(p => p.id === opponent.id)?.boardSkin;
      // Derive the skin name from the opponent's selected premium board (defaults to "Default")
      const opponentSkinName = opponentBoardSkin
        ? PREMIUM_BOARDS.find(b => b.id === opponentBoardSkin)?.skinName || 'Default'
        : 'Default';
      players.push({
        id: opponent.id,
        name: opponent.name || "Player",
        theme: opponentTheme,
        isLocal: false,
        opponent: opponent,
        // Use opponent's own selected skin (show off their premium board)
        boardSkin: opponentSkinName,
      });
    });

    return players;
  }, [playerId, theme, gameState, playerThemes, selectedPremiumBoard, skinName, roomData]);

  // Create online game instance - only recreate when essential dependencies change
  const onlineGame = useMemo(() => {
    if (!game || !playerId) {
      return null;
    }

    const networkGame = new OnlineGame(
      playerId,
      async (eventType, eventData) => {
        // Create a ref to check current state without causing re-renders
        if (networkGame?.isGameOver || !networkGame?.isGameStarted) {
          return;
        }

        try {
          await sendGameEvent(eventType, eventData);
        } catch (error) {
          // If server says game is not in progress, stop local game
          if (error.message?.includes("Game not in progress")) {
            networkGame?.stop();
          }
        }
      },
      game.sounds,
      roomData?.gameSettings || {},
      (opponentId) => {
        // Callback for opponent state changes during animations
        setOpponentRenderTrigger((prev) => prev + 1);
      }
    );

    return networkGame;
  }, [playerId, game?.sounds]); // Removed sendGameEvent and roomData?.gameSettings to prevent recreation

  // Cleanup previous onlineGame instance when new one is created
  const previousOnlineGameRef = useRef(null);
  useEffect(() => {
    if (
      previousOnlineGameRef.current &&
      previousOnlineGameRef.current !== onlineGame
    ) {
      previousOnlineGameRef.current.stop();
    }
    previousOnlineGameRef.current = onlineGame;

    return () => {
      if (onlineGame) {
        onlineGame.stop();
      }
    };
  }, [onlineGame]);

  // Initialize Theme Sound Manager for board-specific sounds
  useEffect(() => {
    if (!onlineGame) return;

    if (!onlineGame.themeSounds) {
      onlineGame.themeSounds = new ThemeSoundManager(onlineGame.sounds);
    }
    const themeConfig = getThemeSoundConfig(selectedPremiumBoard);
    onlineGame.themeSounds.setActiveTheme(selectedPremiumBoard, themeConfig);

    return () => {
      if (onlineGame?.themeSounds) {
        onlineGame.themeSounds.cleanup();
      }
    };
  }, [selectedPremiumBoard, onlineGame]);

  // Start the game when onlineGame is created
  useEffect(() => {
    if (onlineGame && !onlineGame.isGameStarted) {
      onlineGame.start();
    }
  }, [onlineGame]);

  // Monitor local player game over state
  useEffect(() => {
    if (onlineGame?.player?.isGameOver && !localPlayerGameOver) {
      setLocalPlayerGameOver(true);
      // Play game over sound for local player
      game?.sounds?.gameOver?.play();

      // The server will reset ready status when it receives the game_over event
    }
  }, [onlineGame?.player?.isGameOver, localPlayerGameOver, game]);

  // Check if all players are game over to show results
  const checkGameOverConditions = useCallback(() => {
    if (!onlineGame) return;

    // Get local player data with proper name from roomData
    const localPlayerName =
      gameState?.players?.find((p) => p.id === playerId)?.name ||
      roomData?.players?.find((p) => p.id === playerId)?.name ||
      "You";

    const localPlayer = {
      id: playerId,
      name: localPlayerName,
      isGameOver: onlineGame.player?.isGameOver || false,
      score: onlineGame.player?.score || 0,
      lines: onlineGame.player?.lines || 0,
      singleLines: onlineGame.player?.stats?.singleLines || 0,
      doubleLines: onlineGame.player?.stats?.doubleLines || 0,
      tripleLines: onlineGame.player?.stats?.tripleLines || 0,
      tetrisLines: onlineGame.player?.stats?.tetrisLines || 0,
      stats: onlineGame.getGameStats(),
    };

    // Get opponents from OnlineGame with proper names
    const opponents = Array.from(onlineGame.getOpponents() || []).map(
      (opponent) => {
        const opponentName =
          gameState?.players?.find((p) => p.id === opponent.id)?.name ||
          roomData?.players?.find((p) => p.id === opponent.id)?.name ||
          opponent.name ||
          `Player ${opponent.id?.slice(0, 8)}`;

        return {
          ...opponent,
          name: opponentName,
        };
      }
    );

    // Combine all players
    const allPlayers = [localPlayer, ...opponents];

    // Get game settings to determine win condition
    const gameSettings = onlineGame.gameSettings;
    const gameStats = onlineGame.getGameStats();

    // Check game over conditions based on game mode
    let shouldShowResults = false;
    let rankings = [];

    if (
      gameSettings.winCondition === "highest_score" &&
      gameSettings.timeLimit
    ) {
      // Time-based score modes: Show results when time limit reached OR last player has highest score
      const allTimeUp = allPlayers.every((p) => p.stats?.isTimeUp);
      const alivePlayers = allPlayers.filter((p) => !p.isGameOver);

      let lastPlayerWins = false;
      if (alivePlayers.length === 1) {
        // Check if last alive player has CLEARLY HIGHER score than dead players
        const lastAlivePlayer = alivePlayers[0];
        const allScores = allPlayers.map((p) => p.score || p.stats?.score || 0);
        const highestScore = Math.max(...allScores);
        const lastPlayerScore =
          lastAlivePlayer.score || lastAlivePlayer.stats?.score || 0;

        // Don't end if everyone has 0 score - give surviving player a chance to score
        const hasScored = lastPlayerScore > 0 || highestScore > 0;
        lastPlayerWins = hasScored && lastPlayerScore > highestScore;
      }

      shouldShowResults =
        (allTimeUp || lastPlayerWins) && allPlayers.length > 1;

      if (shouldShowResults) {
        // Sort by score (highest first)
        rankings = [...allPlayers].sort((a, b) => {
          const scoreA = a.score || a.stats?.score || 0;
          const scoreB = b.score || b.stats?.score || 0;
          return scoreB - scoreA;
        });
      }
    } else {
      // Survival modes: Show results when only 1 or 0 players are alive
      const alivePlayers = allPlayers.filter((p) => !p.isGameOver);
      const allPlayersGameOver = alivePlayers.length === 0;
      const onlyOneSurvivor = alivePlayers.length === 1;

      // End game if all dead OR only one survivor left
      shouldShowResults =
        (allPlayersGameOver || onlyOneSurvivor) && allPlayers.length > 1;

      if (shouldShowResults) {
        // Sort by survival time (last to die wins), then by score
        rankings = [...allPlayers].sort((a, b) => {
          // If one player is still alive, they win
          if (!a.isGameOver && b.isGameOver) return -1;
          if (a.isGameOver && !b.isGameOver) return 1;

          // If both have same game over status, sort by score
          const scoreA = a.score || a.stats?.score || 0;
          const scoreB = b.score || b.stats?.score || 0;
          return scoreB - scoreA;
        });
      }
    }

    if (shouldShowResults && !showGameResult) {
      setGameResults({
        players: rankings,
        gameMode: gameSettings.gameMode,
        winCondition: gameSettings.winCondition,
      });
      setShowGameResult(true);
    }
  }, [
    onlineGame,
    playerId,
    showGameResult,
    gameState,
    roomData,
    opponentRenderTrigger,
  ]);

  // Note: We don't stop OnlineGame when results are shown because we still need
  // to receive network updates about other players. Game will stop naturally when
  // component unmounts or user returns to lobby.

  // Handle game events
  const handleGameEvent = useCallback(
    async (eventType, eventData) => {
      // Don't send events if game is over or showing results
      if (showGameResult || localPlayerGameOver) {
        return;
      }

      try {
        await sendGameEvent(eventType, eventData);

        // Check for game over conditions after any significant event
        checkGameOverConditions();
      } catch (error) {
        // Don't retry or continue if server says game is not in progress
        if (error.message?.includes("Game not in progress")) {
        }
      }
    },
    [
      sendGameEvent,
      checkGameOverConditions,
      showGameResult,
      localPlayerGameOver,
    ]
  );

  // Build keyboard mapping from player-0's custom key bindings
  const keyboardBindings = useMemo(() => {
    const keyBoardMapping =
      userSavedData?.settings?.controls?.keyBoard ||
      defaultUserData.settings.controls.keyBoard;

    // Map bindingValue to the action names used by OnlineGame.handleInput
    const bindingToAction = {
      ArrowLeft: "left",
      ArrowRight: "right",
      ArrowDown: "down",
      " ": "rotate",
      rotate: "rotate",
      hardDrop: "hardDrop",
      lifeSaver: "lifeSaver",
    };

    const keyMap = {};
    const boundKeys = ["Escape"];

    for (const [key, mapping] of Object.entries(keyBoardMapping)) {
      if (mapping.playerNumber === "0" && mapping.bindingValue) {
        const action = bindingToAction[mapping.bindingValue];
        if (action) {
          keyMap[key] = action;
          boundKeys.push(key);
        }
      }
    }

    return { keyMap, boundKeys };
  }, [userSavedData]);

  // Set up keyboard controls with frame-rate independent timing
  const handleKeyboardInput = useCallback(({ key }) => {
    // Handle Escape key to toggle quit modal
    if (key === "Escape") {
      if (!showGameResult) {
        setShowQuitModal(prev => !prev);
        game?.sounds?.clickSound?.play();
      }
      return;
    }

    // Don't process game controls if modal is open or game over
    if (showQuitModal || !onlineGame || isPaused || localPlayerGameOver) return;

    const action = keyboardBindings.keyMap[key];
    if (action) {
      onlineGame.handleInput(action);
    }
  }, [onlineGame, isPaused, localPlayerGameOver, showQuitModal, showGameResult, game, keyboardBindings]);

  useKeyboard({
    onRawInput: handleKeyboardInput,
    repeatDelay: 150,
    enabled: !isPaused && !localPlayerGameOver,
    keys: keyboardBindings.boundKeys,
    preventDefault: true
  });

  // Set up gamepad controls for Player-0 using useGamepad hook with custom bindings
  const handleGamepadInput = useCallback(({ type, buttonIndex, direction }) => {
    // Handle Start button (button 9) to toggle quit modal
    if (type === 'button' && buttonIndex === 9) {
      if (!showGameResult) {
        setShowQuitModal(prev => !prev);
        game?.sounds?.clickSound?.play();
      }
      return;
    }

    // Don't process game controls if modal is open or game over
    if (showQuitModal || !onlineGame || isPaused || localPlayerGameOver) return;

    // Get controller bindings for Player-0 with fallback to defaults
    const controllerBindings =
      userSavedData?.settings?.controls?.controller?.[0] ||
      defaultUserData.settings.controls.controller[0];

    if (type === 'button') {
      // Map button to action using saved bindings
      const bindingValue = controllerBindings[buttonIndex];

      if (bindingValue) {
        let action = bindingValue;

        // Convert arrow key bindings to action names
        if (bindingValue === "ArrowDown") {
          action = "down";
        } else if (bindingValue === "ArrowLeft") {
          action = "left";
        } else if (bindingValue === "ArrowRight") {
          action = "right";
        }

        // Execute the action
        onlineGame.handleInput(action);
      }
    }
    // Note: Navigation (D-pad/analog) is handled through button bindings
  }, [onlineGame, isPaused, localPlayerGameOver, userSavedData, showQuitModal, showGameResult, game]);

  useGamepad({
    onRawInput: handleGamepadInput,
    repeatDelay: 150,
    enabled: !isPaused && !localPlayerGameOver,
    gamepadIndex: null // null = find any connected gamepad
  });

  // Initialize game timer when game starts - continue running even if local player is game over
  useEffect(() => {
    if (!onlineGame || !onlineGame.isGameStarted || showGameResult) return;

    const timer = setInterval(() => {
      // Force re-render for InGameStats to show updated time
      setGameState((prev) => ({ ...prev, lastUpdate: Date.now() }));

      // Check game over conditions periodically (only if not showing results)
      if (!showGameResult) {
        checkGameOverConditions();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [onlineGame?.isGameStarted, checkGameOverConditions, showGameResult]);

  // Handle pause/resume logic - but don't pause the game if local player is just watching
  useEffect(() => {
    if (!onlineGame || localPlayerGameOver) return; // Don't pause if just spectating

    if (isPaused) {
      onlineGame.pause();
    } else {
      onlineGame.resume();
    }
  }, [onlineGame, isPaused, localPlayerGameOver]);

  // Listen to network events and update opponents
  useEffect(() => {
    const handleGameEvent = (eventData) => {
      if (eventData.playerId === playerId) return; // Ignore own events

      // Update opponent data in OnlineGame
      const opponentUpdate = {
        id: eventData.playerId,
        ...eventData.eventData,
      };

      // Removed old heavy event handlers (hard_drop_complete, piece_placed)
      // New system handles these through input events and local simulation

      // Removed old laser animation and line clearing event handlers
      // New system: Opponents process input events locally and run their own laser animations

      // Handle new input-based events (Phase 2)
      if (eventData.eventType === "player_input") {
        const { action, inputId, timestamp, data } = eventData.eventData;

        // Pass to OnlineGame for local processing and instant visual feedback
        const success = onlineGame?.handleOpponentInput(
          eventData.playerId,
          action,
          inputId,
          timestamp,
          data
        );

        if (success) {
          // Force immediate re-render for responsive input feedback
          setOpponentRenderTrigger((prev) => prev + 1);
        }

        // Don't do general opponent update - simulator already handled it
        return;
      }

      // Handle state sync events (Phase 3)
      if (eventData.eventType === "state_sync") {
        // Pass to OnlineGame for desync detection and correction
        onlineGame?.handleOpponentStateSync(
          eventData.playerId,
          eventData.eventData
        );

        // Force re-render in case of desync correction
        setOpponentRenderTrigger((prev) => prev + 1);

        // Don't do general opponent update - handleOpponentStateSync already handled it
        return;
      }

      // Special handling for life saver events
      if (eventData.eventType === "life_saver_used") {
        const { rowsCleared, remainingLifeSavers } = eventData.eventData;

        // Show life saver notification
        if (window.toast) {
          window.toast.info(
            `🛟 ${
              eventData.playerName || "Player"
            } used Life Saver! Cleared ${rowsCleared} rows.`,
            {
              position: "top-right",
              autoClose: 3000,
            }
          );
        }

        // Update opponent data with remaining life savers
        opponentUpdate.lifeSaverCount = remainingLifeSavers;

        // Force immediate re-render to show the effect
        setOpponentRenderTrigger((prev) => prev + 1);
      }

      // Special handling for game over events
      if (eventData.eventType === "game_over") {
        opponentUpdate.isGameOver = true;
        opponentUpdate.quitReason = eventData.eventData?.reason || "died";
      }

      onlineGame?.updateOpponent(eventData.playerId, opponentUpdate);

      // New system: Only need to handle remaining events (game_over, state_sync already handled above)
      // Most events are now processed by input system with immediate rendering
      // Throttled re-render for remaining events (60fps max)
      if (
        !onlineGame._lastOpponentRenderUpdate ||
        Date.now() - onlineGame._lastOpponentRenderUpdate > 16
      ) {
        setOpponentRenderTrigger((prev) => prev + 1);
        onlineGame._lastOpponentRenderUpdate = Date.now();
      }

      // Trigger game over check after updating opponent
      checkGameOverConditions();
    };

    const handlePlayerDisconnectedInGame = (eventData) => {
      // Remove disconnected player from opponents
      if (onlineGame) {
        onlineGame.removeOpponent(eventData.playerId);
      }

      // Show disconnection notification via toast
      const gameMode = onlineGame?.gameSettings?.gameMode || "survival";
      const contextMessages = {
        time_attack: "The game will continue with remaining players.",
        survival: "They have been eliminated from the survival challenge.",
        speed_battle: "The speed battle continues with remaining players.",
      };
      const context = contextMessages[gameMode] || "The game continues with remaining players.";

      toastService.show(
        "Player Disconnected",
        `${eventData.playerName} has left the game. ${context}`,
        "warning",
        { autoClose: 5000, pauseOnHover: true }
      );

      // Trigger game over check
      checkGameOverConditions();
    };

    // Note: Player quit is now handled via game_over events with reason="quit"

    const handleGameEnded = (eventData) => {
      if (showGameResult) {
        return;
      }

      // Convert server rankings to local format with proper names
      const rankings = eventData.rankings.map((ranking) => {
        const playerName =
          ranking.playerId === playerId
            ? gameState?.players?.find((p) => p.id === playerId)?.name ||
              roomData?.players?.find((p) => p.id === playerId)?.name ||
              "You"
            : gameState?.players?.find((p) => p.id === ranking.playerId)
                ?.name ||
              roomData?.players?.find((p) => p.id === ranking.playerId)?.name ||
              ranking.playerName;

        return {
          id: ranking.playerId,
          name: playerName,
          score: ranking.score,
          lines: ranking.lines,
          isGameOver: ranking.isGameOver,
          singleLines: ranking.singleLines,
          doubleLines: ranking.doubleLines,
          tripleLines: ranking.tripleLines,
          tetrisLines: ranking.tetrisLines,
          stats: {
            score: ranking.score,
            linesCleared: ranking.lines,
            singleLines: ranking.singleLines,
            doubleLines: ranking.doubleLines,
            tripleLines: ranking.tripleLines,
            tetrisLines: ranking.tetrisLines,
          },
        };
      });

      setGameResults({
        players: rankings,
        gameMode: onlineGame?.gameSettings?.gameMode || "survival",
        winCondition: onlineGame?.gameSettings?.winCondition || "last_survivor",
      });
      setShowGameResult(true);
    };

    if (onlineGame) {
      // Subscribe to network events via NetworkService
      const networkService = require("../../services/NetworkService").default;
      networkService.on("game_event", handleGameEvent);
      networkService.on(
        "player_disconnected_ingame",
        handlePlayerDisconnectedInGame
      );
      // Note: player_quit is handled as game_over event with reason
      networkService.on("game_ended", handleGameEnded);

      return () => {
        networkService.off("game_event", handleGameEvent);
        networkService.off(
          "player_disconnected_ingame",
          handlePlayerDisconnectedInGame
        );
        // Note: player_quit is handled as game_over event with reason
        networkService.off("game_ended", handleGameEnded);
      };
    }

    return () => {
      // Cleanup
    };
  }, [
    onlineGame,
    playerId,
    checkGameOverConditions,
    showGameResult,
    gameState,
    roomData,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (onlineGame) {
        onlineGame.stop();
      }
    };
  }, [onlineGame]);

  // Handle return to lobby
  const handleReturnToLobby = () => {
    setShowGameResult(false);
    setGameResults(null);
    setLocalPlayerGameOver(false);

    // Stop the current game
    if (onlineGame) {
      onlineGame.stop();
    }

    // Reset game state and go back to lobby (not exit room)
    // This should go back to OnlineArena, then to RoomLobby
    onBack();
  };

  const handleGoBack = () => {
    if (onlineGame?.isGameStarted) {
      // Show quit confirmation modal when game is active
      setShowQuitModal(true);
    } else {
      // If game not started yet, go back without confirmation
      onBack();
    }
  };

  const handleQuitGame = () => {
    onBack();
  };

  // Show game result screen if game is over
  if (showGameResult) {
    return (
      <OnlineGameResult
        gameResults={gameResults}
        playerId={playerId}
        onReturnToLobby={handleReturnToLobby}
        game={game}
      />
    );
  }

  if (!onlineGame) {
    return (
      <div className={styles.pageContainer}>
        <CardOne>
          <div className={styles.loadingContainer}>
            <p>Loading game...</p>
            <p>
              Waiting for:{" "}
              {!game
                ? "game context"
                : !playerId
                ? "player ID"
                : "initialization"}
            </p>
          </div>
        </CardOne>
      </div>
    );
  }

  // Remove the "Starting game..." screen - start immediately
  // if (onlineGame && !onlineGame.isGameStarted && !localPlayerGameOver) {
  //   return (
  //     <div className={styles.pageContainer}>
  //       <CardOne>
  //         <div className={styles.loadingContainer}>
  //           <p>Starting game...</p>
  //         </div>
  //       </CardOne>
  //     </div>
  //   );
  // }

  return (
    <div className={styles.pageContainer}>
      {/* Game Mode Stats Overlay */}
      <OnlineInGameStats onlineGame={onlineGame} />

      {/* Disconnection Notification */}
      {disconnectionNotification && (
        <DisconnectionNotification
          playerName={disconnectionNotification.playerName}
          gameMode={disconnectionNotification.gameMode}
          onDismiss={() => setDisconnectionNotification(null)}
        />
      )}

      <CardOne customTag="section" attributes={{ className: styles.mainCard }}>
        <div className={styles.fixedControls}>
          <GoBackButton onClickHandler={handleGoBack} game={game} />
        </div>

        {isPaused && (
          <div className={styles.pauseOverlay}>
            <div
              className={`${styles.pauseMenu} ${
                styles[`pauseMenu-theme-${theme}`]
              }`}
            >
              <FontAwesomeIcon icon={faPause} className={styles.pauseIcon} />
              <h2>Game Paused</h2>
              <div className={styles.pauseButtons}>
                <button
                  className={`${styles.pauseButton} ${
                    styles[`pauseButton-theme-${theme}`]
                  }`}
                  onClick={() => setIsPaused(false)}
                >
                  Resume
                </button>
                <button
                  className={`${styles.pauseButton} ${
                    styles[`pauseButton-theme-${theme}`]
                  }`}
                  onClick={handleQuitGame}
                >
                  Quit Game
                </button>
              </div>
            </div>
          </div>
        )}

        <div className={styles.gameContainer}>
          {/* Render each player wrapped in their themed background */}
          {allPlayersWithThemes.map((player, index) => {
            // Extract theme number from theme string like "theme-0"
            const themeNumber =
              typeof player.theme === "string"
                ? player.theme.replace("theme-", "")
                : player.theme.toString();
            const themeClass = `playerBackgroundTheme${themeNumber}`;

            return (
              <div
                key={player.id}
                className={`${styles.playerSection} ${styles[themeClass]}`}
                data-theme={player.theme}
                data-player={player.name}
              >
                {player.isLocal ? (
                  // Local Player
                  onlineGame && (
                    <OnlinePlayer
                      onlineGame={onlineGame}
                      playerId={playerId}
                      gameState={gameState}
                      onGameEvent={handleGameEvent}
                    />
                  )
                ) : (
                  // Opponent Player
                  <OpponentPanel
                    opponent={
                      onlineGame
                        ?.getOpponents()
                        ?.find((opp) => opp.id === player.id) ||
                      gameState?.players?.find((p) => p.id === player.id) ||
                      player.opponent
                    }
                    opponentTheme={player.theme}
                    opponentBoardSkin={player.boardSkin}
                    gameState={gameState}
                    roomData={roomData}
                    onlineGame={onlineGame}
                  />
                )}
              </div>
            );
          })}
        </div>
      </CardOne>

      {/* Quit Game Confirmation Modal */}
      <ConfirmationModal
        isVisible={showQuitModal}
        message="Are you sure you want to quit the game? You will lose your progress."
        confirmText="Quit Game"
        confirmButtonStyle="danger"
        onConfirm={async () => {
          setShowQuitModal(false);

          // Send quit event to notify other players
          try {
            // Mark local player as game over immediately
            if (onlineGame) {
              onlineGame.player.isGameOver = true;
            }

            await sendGameEvent("game_over", {
              playerId: playerId,
              finalScore: onlineGame?.player?.score || 0,
              level: onlineGame?.player?.level || 1,
              lines: onlineGame?.player?.lines || 0,
              singleLines: onlineGame?.player?.stats?.singleLines || 0,
              doubleLines: onlineGame?.player?.stats?.doubleLines || 0,
              tripleLines: onlineGame?.player?.stats?.tripleLines || 0,
              tetrisLines: onlineGame?.player?.stats?.tetrisLines || 0,
              reason: "quit", // Add quit reason
            });
          } catch (error) {}

          if (onlineGame) {
            onlineGame.stop();
          }
          onBack();
        }}
        onCancel={() => setShowQuitModal(false)}
      />
    </div>
  );
};
